import { lazy, Suspense, useEffect, useRef, useContext } from 'react';

/// Components
import { connect, useDispatch } from 'react-redux';
import { Route, Routes, useLocation, useNavigate, useParams, Navigate, Outlet } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// action
import { checkAutoLogin } from './services/AuthService';
import { isAuthenticated } from './store/selectors/AuthSelectors';
import { ThemeContext } from './context/ThemeContext';
/// Style
import "./vendor/bootstrap-select/dist/css/bootstrap-select.min.css";
import "./css/style.css";
import "./jsx/index.css";
import "./jsx/chart.css";
import "./jsx/step.css";

// Import components
import Nav from "./jsx/layouts/nav";
import ScrollToTop from "./jsx/layouts/ScrollToTop";

const SignUp = lazy(() => import('./jsx/pages/Registration'));
const ForgotPassword = lazy(() => import('./jsx/pages/ForgotPassword'));
const Login = lazy(() => {
    return new Promise(resolve => {
        setTimeout(() => resolve(import('./jsx/pages/Login')), 500);
    });
});

const Trades = lazy(() => import('./jsx/components/trades'));
const PuzzleLive = lazy(() => import('./jsx/components/Puzzle/PuzzleLive'));
const PuzzleJobs = lazy(() => import('./jsx/components/Puzzle/PuzzleJobs'));
const PuzzleTestQueue = lazy(() => import('./jsx/components/Puzzle/PuzzleTestQueue'));
const TradeDetail = lazy(() => import('./jsx/components/trades/TradeDetail'));
const Copilots = lazy(() => import('./jsx/components/Copilots'));
const Connections = lazy(() => import('./jsx/components/Connections'));
const Statistics = lazy(() => import('./jsx/components/Statistics'));
const BusinessPlan = lazy(() => import('./jsx/components/BusinessPlan'));
const Settings = lazy(() => import('./jsx/components/Settings'));
const Orquestador = lazy(() => import('./jsx/components/Orquestador'));
const Portfolio = lazy(() => import('./jsx/components/Portfolio'));
const Backtest = lazy(() => import('./jsx/components/Backtest'));
const BacktestDetailRouter = lazy(() => import('./jsx/components/Backtest/BacktestDetailRouter'));
const BacktestTradeDetail = lazy(() => import('./jsx/components/Backtest/BacktestTradeDetail'));
const BacktestCycles = lazy(() => import('./jsx/components/Backtest/BacktestCycles'));
const BacktestPuzzle = lazy(() => import('./jsx/components/Backtest/BacktestPuzzle'));
const BacktestPuzzleStats = lazy(() => import('./jsx/components/Backtest/BacktestPuzzleStats'));
const BacktestRegimeView = lazy(() => import('./jsx/components/Backtest/BacktestRegimeView'));
const BacktestPuzzleZone = lazy(() => import('./jsx/components/Backtest/BacktestPuzzleZone'));
const BacktestCycleDetail = lazy(() => import('./jsx/components/Backtest/BacktestCycleDetail'));
const ChangelogPage = lazy(() => import('./jsx/components/Backtest/ChangelogPage'));
const BacktestDetailLegacy = lazy(() => import('./jsx/components/Backtest/_legacy/BacktestDetailLegacy'));
const BacktestTradeDetailLegacy = lazy(() => import('./jsx/components/Backtest/_legacy/BacktestTradeDetailLegacy'));
const BacktestImpulseConfluencesTradeDetail = lazy(() => import('./jsx/components/Backtest/_legacy/BacktestImpulseConfluencesTradeDetail'));
const SqAtrPullbackTradeDetail = lazy(() => import('./jsx/components/Backtest/SqAtrPullbackTradeDetail'));
const Order = lazy(() => import('./jsx/components/Report/Order'));
const Reports = lazy(() => import('./jsx/components/Report/Reports'));
const User = lazy(() => import('./jsx/components/Report/User'));
const Contact = lazy(() => import('./jsx/components/Report/Contact'));
const Activity = lazy(() => import('./jsx/components/Report/Activity'));
const LockScreen = lazy(() => import('./jsx/pages/LockScreen'));
const Error404 = lazy(() => import('./jsx/pages/Error404'));

function withRouter(Component) {
    function ComponentWithRouterProp(props) {
        let location = useLocation();
        let navigate = useNavigate();
        let params = useParams();

        return (
            <Component
                {...props}
                router={{ location, navigate, params }}
            />
        );
    }

    return ComponentWithRouterProp;
}

// Protected route wrapper
function ProtectedRoute({ children, isAuthenticated }) {
    console.log('🔒 [ProtectedRoute] Checking authentication:', { isAuthenticated });
    
    // Check both Redux state and localStorage as fallback
    // This handles cases where Redux hasn't updated yet after login
    const checkAuth = () => {
        if (isAuthenticated) {
            console.log('✅ [ProtectedRoute] Authenticated via Redux state');
            return true;
        }
        
        // Fallback: check localStorage directly
        const userDetails = localStorage.getItem('userDetails');
        console.log('🔍 [ProtectedRoute] Checking localStorage fallback:', { userDetails: userDetails ? 'present' : 'missing' });
        if (userDetails) {
            try {
                const parsed = JSON.parse(userDetails);
                const hasToken = !!(parsed.access_token || parsed.idToken);
                console.log('🔍 [ProtectedRoute] Parsed localStorage:', { 
                    access_token: parsed.access_token ? 'present' : 'missing',
                    idToken: parsed.idToken ? 'present' : 'missing',
                    hasToken 
                });
                if (hasToken) {
                    console.log('✅ [ProtectedRoute] Authenticated via localStorage fallback');
                    return true;
                }
            } catch (e) {
                console.error('❌ [ProtectedRoute] Error parsing localStorage:', e);
            }
        }
        console.log('❌ [ProtectedRoute] Not authenticated, redirecting to /login');
        return false;
    };
    
    if (!checkAuth()) {
        return <Navigate to="/login" replace />;
    }
    console.log('✅ [ProtectedRoute] Allowing access');
    return children;
}

// Main layout component for authenticated routes
function MainLayout() {
    const { menuToggle } = useContext(ThemeContext);

    return (
        <div id="main-wrapper" className={`show ${menuToggle ? "menu-toggle" : ""}`}>
            <Nav />
            <div className="content-body" style={{ minHeight: window.screen.height - 45, paddingRight: 0 }}>
                <div className="container-fluid">
                    <Outlet />
                </div>
            </div>
        </div>
    );
}

function App(props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const hasCheckedAutoLoginRef = useRef(false);

    console.log('🔄 [App] Render:', { 
        isAuthenticated: props.isAuthenticated, 
        pathname: location.pathname,
        hasCheckedAutoLogin: hasCheckedAutoLoginRef.current 
    });

    useEffect(() => {
        // Only run checkAutoLogin once on mount to restore session from localStorage
        // This prevents interference with the login flow after successful login
        if (!hasCheckedAutoLoginRef.current) {
            console.log('🚀 [App] First mount, checking auto login...');
            // Always check localStorage on mount, even if Redux says we're authenticated
            // This ensures localStorage and Redux are in sync
            if (!props.isAuthenticated) {
                console.log('🚀 [App] User not authenticated, calling checkAutoLogin...');
                checkAutoLogin(dispatch, navigate);
            } else {
                console.log('🚀 [App] User already authenticated on mount');
            }
            hasCheckedAutoLoginRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    // Auto-redirect when authentication state changes (only for logout)
    useEffect(() => {
        console.log('🧭 [App] Redirect useEffect triggered:', { 
            isAuthenticated: props.isAuthenticated, 
            pathname: location.pathname,
            hasCheckedAutoLogin: hasCheckedAutoLoginRef.current 
        });
        
        // Skip redirect check on initial mount (handled by checkAutoLogin)
        if (!hasCheckedAutoLoginRef.current) {
            console.log('🧭 [App] Skipping redirect check (initial mount)');
            return;
        }

        // Only redirect to login if user logged out and is on protected routes
        // Login redirect is handled directly in loginAction to avoid timing issues
        if (!props.isAuthenticated && (location.pathname.startsWith('/trades') || location.pathname === '/' || location.pathname === '/orders' || location.pathname === '/reports' || location.pathname === '/user' || location.pathname === '/contact' || location.pathname === '/activity')) {
            console.log('🧭 [App] User not authenticated and on protected route, redirecting to /login');
            navigate('/login', { replace: true });
        } else {
            console.log('🧭 [App] No redirect needed');
        }
    }, [props.isAuthenticated, location.pathname, navigate]);

    return (
        <Suspense fallback={
            <div id="preloader">
                <div className="sk-three-bounce">
                    <div className="sk-child sk-bounce1"></div>
                    <div className="sk-child sk-bounce2"></div>
                    <div className="sk-child sk-bounce3"></div>
                </div>
            </div>
        }>
            <Routes>
                {/* Public routes */}
                <Route path='/login' element={
                    props.isAuthenticated ? <Navigate to="/trades" replace /> : <div className="vh-100"><Login /></div>
                } />
                <Route path='/page-register' element={
                    props.isAuthenticated ? <Navigate to="/trades" replace /> : <div className="vh-100"><SignUp /></div>
                } />
                <Route path='/page-forgot-password' element={
                    props.isAuthenticated ? <Navigate to="/trades" replace /> : <div className="vh-100"><ForgotPassword /></div>
                } />

                {/* Protected routes */}
                <Route path='/' element={
                    <ProtectedRoute isAuthenticated={props.isAuthenticated}>
                        <Navigate to="/trades" replace />
                    </ProtectedRoute>
                } />
                <Route element={
                    <ProtectedRoute isAuthenticated={props.isAuthenticated}>
                        <MainLayout />
                    </ProtectedRoute>
                }>
                    <Route path='/trades' element={<Trades />} />
                    <Route path='/trades/:id' element={<TradeDetail />} />
                    <Route path='/puzzle' element={<PuzzleLive />} />
                    <Route path='/puzzle/jobs' element={<PuzzleJobs />} />
                    <Route path='/puzzle/tests' element={<PuzzleTestQueue />} />
                    <Route path='/puzzle/:symbol/level/:levelKey' element={<BacktestPuzzleZone />} />
                    <Route path='/puzzle/:symbol/zone' element={<BacktestPuzzleZone />} />
                    <Route path='/copilots' element={<Copilots />} />
                    <Route path='/connections' element={<Connections />} />
                    <Route path='/statistics' element={<Statistics />} />
                    <Route path='/business-plan' element={<BusinessPlan />} />
                    <Route path='/settings' element={<Settings />} />
                    <Route path='/orquestador' element={<Orquestador />} />
                    <Route path='/portfolio' element={<Portfolio />} />
                    <Route path='/backtest' element={<Backtest />} />
                    <Route path='/backtest/changelog/:strategyCode' element={<ChangelogPage />} />
                    <Route path='/backtest/:id' element={<BacktestDetailRouter />} />
                    <Route path='/backtest/:id/trade/:tradeId' element={<BacktestTradeDetail />} />
                    <Route path='/backtest/:id/cycles' element={<BacktestCycles />} />
                    <Route path='/backtest/:id/puzzle' element={<BacktestPuzzle />} />
                    <Route path='/backtest/:id/puzzle-stats' element={<BacktestPuzzleStats />} />
                    <Route path='/backtest/:id/regime' element={<BacktestRegimeView />} />
                    <Route path='/backtest/:id/puzzle/trade/:tradeId' element={<BacktestPuzzle />} />
                    <Route path='/backtest/:id/puzzle/zone/:zoneId' element={<BacktestPuzzleZone />} />
                    <Route path='/backtest/:id/puzzle/level/:levelKey' element={<BacktestPuzzleZone />} />
                    <Route path='/backtest/:id/cycle/:cycleIndex' element={<BacktestCycleDetail />} />
                    <Route path='/backtest-ifl/:id' element={<BacktestDetailLegacy />} />
                    <Route path='/backtest-ifl/:id/trade/:tradeId' element={<BacktestTradeDetailLegacy />} />
                    <Route path='/backtest-ic/:id/trade/:tradeId' element={<BacktestImpulseConfluencesTradeDetail />} />
                    <Route path='/backtest-sqatr/:id/trade/:tradeId' element={<SqAtrPullbackTradeDetail />} />
                    <Route path='/orders' element={<Order />} />
                    <Route path='/reports' element={<Reports />} />
                    <Route path='/user' element={<User />} />
                    <Route path='/contact' element={<Contact />} />
                    <Route path='/activity' element={<Activity />} />
                </Route>

                {/* Other routes */}
                <Route path='/page-lock-screen' element={<LockScreen />} />
                <Route path='/page-error-404' element={<Error404 />} />
                
                {/* Catch all - redirect to login or trades */}
                <Route path='*' element={
                    props.isAuthenticated ? <Navigate to="/trades" replace /> : <Navigate to="/login" replace />
                } />
            </Routes>
            <ScrollToTop />
            <ToastContainer 
              position="top-center"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="dark"
            />
        </Suspense>
    );
};

const mapStateToProps = (state) => {
    return {
        isAuthenticated: isAuthenticated(state),
    };
};

//export default connect((mapStateToProps)(App));
export default withRouter(connect(mapStateToProps)(App));
